<template lang="pug">
.passport-details()
  .row
    Card.cards.visualisations
      template(#content)
        .section-title
          .line
          h3 Improvement overview
          .line
        .scenario-cards
          Card.sub-card
            template(#content)
              .content-wrapper
                .content
                  h3.scenario-title
                    span.pi.pi-bolt
                    | Energy Usage
                  ul
                    li
                      span.prop Non-renewable energy usage:
                      span.value
                          |{{ initialConsumption }} ({{ initialYear }})
                          | →
                          |{{ finalConsumption }} ({{ endYear }}) kWh/m²·yr
                    li
                      span.prop Reduction:
                      span.value {{getReductionPercentage(initialConsumption, finalConsumption)}}%
          Card.sub-card
            template(#content)
              .content-wrapper
                .content
                  h3.scenario-title
                    span.pi.pi-cloud
                    | GHG Emissions
                  ul
                    li
                      span.prop Total Emissions:
                      span.value
                        | {{stagesChartData[0].globalCO2emissions}}  ({{initialYear}})
                        | →
                        | {{stagesChartData[stagesChartData.length - 1].globalCO2emissions}} ({{endYear}}) tCO₂/yr
                    li
                      span.prop Reduction:
                      span.value {{getReductionPercentage(stagesChartData[0].globalCO2emissions, stagesChartData[stagesChartData.length - 1].globalCO2emissions)}}%

          Card.sub-card
            template(#content)
              .content-wrapper
                .content
                  h3.scenario-title
                    span.pi.pi-calculator
                    | Economics
                  ul
                    li
                      span.prop Estimated energy bill:
                      span.value
                        | {{stagesChartData[0].energyBillGlobal}}€  ({{initialYear}})
                        | →
                        | {{stagesChartData[stagesChartData.length - 1].energyBillGlobal}}€ ({{endYear}})
                    li
                      span.prop Estimated annual savings:
                      span.value {{stagesChartData[0].energyBillGlobal - stagesChartData[stagesChartData.length - 1].energyBillGlobal}}€
                    li
                      span.prop Reduction:
                      span.value {{getReductionPercentage(stagesChartData[0].energyBillGlobal, stagesChartData[stagesChartData.length - 1].energyBillGlobal)}}%

        BrVisualisations(:stagesChartData="stagesChartData" :endYear="endYear")

        .final-rating-wrapper
          .final-rating
            .text Final Energy Rating:
            span.rating-label(:style="{backgroundColor: (getRatingColor(getRating(finalConsumption)) || 'white'), color: pickTextOn(getRatingColor(getRating(finalConsumption)))}") {{getRating(finalConsumption)}}
</template>
<script setup lang="ts">
import Card from "primevue/card";
import { storeToRefs } from "pinia";
import {usePassport, useRecommendations} from "@/store";
import BrVisualisations from "@/components/result/br-passport/BrVisualisations.vue";
import {formatPercentageToString, getRatingColor, getRating} from "@/helpers";
import type {IStagesChartData} from "@/types";
import type ComputedRef from "vue";
import {computed} from "vue";
import {pickTextOn} from "@/helpers";

const { response } = storeToRefs(usePassport());
const { report } = storeToRefs(useRecommendations());
const initialConsumption = computed(() => response.value.stages.stage0.scenarioResulting?.results.find((r) => r.id_family == "ES.EPC")?.Parameters.GlobalEPNRconsumption as number)
const finalConsumption = computed(() => response.value.stages[`stage${response.value.plan.userPreferences.strategyDetails.stages - 1}`].scenarioResulting?.results.find((r) => r.id_family == "ES.EPC")?.Parameters.GlobalEPNRconsumption as number)

const initialYear = computed(() => response.value?.plan?.userPreferences?.strategyDetails?.["intervals=(stages)"].stage0.start || '')
const endYear = computed(() => response.value?.plan?.userPreferences?.strategyDetails?.objectiveYear)

const stagesChartData: ComputedRef<IStagesChartData[]> = computed(() => Object.values(response.value.stages).map((v) => {
  const res = v.scenarioResulting?.results
  return {
    number: v.number,
    year: v.year,
    globalEPNRconsumption: res ? Number(res?.find((r) => r.id_family == "ES.EPC")?.Parameters.GlobalEPNRconsumption) : -1,
    letterEPNRconsumption: res ? res?.find((r) => r.id_family == "ES.EPC")?.Parameters.LetterEPNRconsumption as string : '',
    globalCO2emissions: res ? Number(res?.find((r) => r.id_family == "ES.EPC")?.Parameters.GlobalCO2emissions) : -1,
    letterCO2emissions: res ? res?.find((r) => r.id_family == "ES.EPC")?.Parameters.LetterCO2emissions as string : '',
    energyBillGlobal: res ? Number(res?.find((r) => r.id_family == "ES.energyCost")?.Parameters.energyBillGlobal) : -1,
  }
}).sort((a, b) => a.number - b.number))

function getReductionPercentage(init: number, end: number) {
  return formatPercentageToString((end-init)/init*100*-1, {maximumFractionDigits: 2})
}
</script>
<style lang="scss" scoped>
.passport-details {
  margin-bottom: 4.5rem;
  :deep(.p-card-content) {
    padding: 2rem 3rem;
  }
  .intro:deep(.p-card-content) {
    padding: 1.5rem 3rem 2rem;
  }
  .cards {
    background-color: $background-color-default;
  }
  .row {
    margin-top: 2rem;
  }
  .cards-row {
    display: grid;
    gap: 3rem;
    grid-template-columns: repeat(1, 1fr);
    margin-top: 2rem;
    @media (min-width: 800px) {
      grid-template-columns: repeat(2, 1fr);
    }
    .section-title {
      margin-top: 0;
    }
    :deep(.p-card-content) {
      padding: 0;
      .data,
      .config-list {
        padding: 0.25rem 1rem 1rem;
      }
    }
  }
  .cards-row {
    :deep(.p-card) {
      box-shadow: none;
      padding: 0;
    }
  }
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .download-button {
      font-size: 1.2rem;
      padding: 0.75rem 2rem;
    }
  }
  .subtitle {
    font-size: 1rem;
    padding: 1rem 0;
    text-align: justify;
  }
  .section-title {
    font-size: 1.4rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1rem auto 2rem;

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin: 0;
    }

    .line {
      height: 0.2rem;
      background-color: $primary-color-light;
      min-width: 1.25rem;

      &:nth-child(3) {
        flex-grow: 1;
      }
    }
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0.5rem;

      span.prop {
        font-weight: 600;
        color: $primary-color;
        margin-right: 0.5rem;
      }

      span.value {
        color: $font-color-default;
      }
    }
  }
  .scenario-cards {
    padding-top: 1rem;
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 1fr;/*
    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (min-width: 1700px) {
      grid-template-columns: repeat(4, 1fr);
    }*/

    :deep(.p-card-content) {
      padding: 1rem;
      padding-top: 0;
    }
    .sub-card {
      box-shadow: none;
      border: 0;
      h3.scenario-title {
        min-width: 15rem;
        margin-top: 0;
        color: $secondary-color-hover;
        font-variant: all-small-caps;
        font-size: 1.5rem;
        span {
          font-size: 1.2rem;
          margin-right: 0.5rem;
          position: relative;
          top: 0.1rem;
          padding: 0.45rem;
          border: 1px solid;
          border-radius: 1.25rem;
        }
      }
      .content-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;

        .content {
          display: flex;
          flex-direction: column;
          ul {
            flex-grow: 1;
          }
        }
      }
    }
  }
  .timeframe {
    :deep(.summary) {
      position: relative;
      top: 0.75rem;
      margin: 3rem 0;
    }
    :deep(.panel-grid) {
      padding: 0;
      gap: 3rem;
      .p-card {
        .p-card-content {
          border: 1px solid var(--berwow-border-color);
          border-top: none;
          padding: 1rem;
          padding-top: 0;
          &:first-child {
            padding-left: 0;
          }
          &:last-child {
            padding-right: 0;
          }
          .title {
            color: $primary-color-hover;
            position: relative;
            bottom: 1.75rem;
            .line {
              flex-grow: 1;
              opacity: 0.3;
              background: var(--berwow-secondary-color-hover);
            }
          }
          .dropzone {
            position: relative;
            bottom: 1.25rem;
            padding: 0 1rem;
            gap: 1rem;
          }
          .year-panel {
            padding: 0;
          }
          .bottom {
            margin: 0 1rem;
            padding: 0.5rem 0.5rem 0;
            border-top: 1px solid $border-color;
            justify-content: flex-end;
          }
        }
      }
    }
  }
  .visualisations {
    padding-bottom: 1rem;
    .section-title {
      margin-bottom: 1rem;
    }
  }
}
.details {
  .data {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
}
.rating-label {
  color: $font-color-contrast;
  font-weight: 500;
  font-size: 1.2rem;
  padding: 1px 0.5rem 1px 4rem !important;
  -webkit-clip-path: polygon(
    0 50%,
    0.6rem 100%,
    0 100%,
    100% 100%,
    100% 0,
    0.6rem 0
  );
  clip-path: polygon(0 50%, 0.6rem 100%, 0 100%, 100% 100%, 100% 0, 0.6rem 0);
}

.final-rating-wrapper {
  display: flex;
  justify-content: end;
  border-top: 1px solid $border-color;
  margin: 1.25rem;
  @media (min-width: 620px) {
    margin: 1.25rem 5rem 0;
  }
  .final-rating {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    padding: 0.75rem;
    margin-top: 2rem;
    color: $primary-color-hover;
  }
}
</style>
